import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Avatar, AvatarFallback, getInitials } from '../avatar';
import { Badge } from '../badge';
import { Button } from '../button';
import { Icon } from '../primitives/Icon';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'reviewing';

export interface ApprovalCardProps {
  title: ReactNode;
  requestedBy: string;
  /** Free-form display string. The component does not parse or format dates. */
  requestedDate: string;
  status?: ApprovalStatus;
  /** Optional key/value rows rendered in a label/value table beneath the header. */
  details?: Record<string, ReactNode>;
  onApprove?: () => void;
  onReject?: () => void;
  onReview?: () => void;
  className?: string;
}

const STATUS_MAP: Record<
  ApprovalStatus,
  { label: string; variant: 'warning' | 'success' | 'danger' | 'purple' }
> = {
  pending: { label: 'Pending review', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
  reviewing: { label: 'In review', variant: 'purple' },
};

/**
 * Approval workflow card. Header carries title + requestor + status
 * chip; optional `details` slot renders a key/value table; `pending`
 * status surfaces Review / Reject / Approve actions in the footer.
 */
export function ApprovalCard({
  title,
  requestedBy,
  requestedDate,
  status = 'pending',
  details,
  onApprove,
  onReject,
  onReview,
  className,
}: ApprovalCardProps) {
  const s = STATUS_MAP[status];
  const initials = getInitials(requestedBy);
  return (
    <article
      className={cn(
        'flex flex-col gap-4 rounded-lg border border-border bg-bg-surface p-5 shadow-sm',
        className,
      )}
    >
      <header className="flex items-start gap-4">
        <div className="flex flex-1 flex-col gap-2">
          <div className="text-h3 text-text-primary">{title}</div>
          <div className="flex items-center gap-2 text-caption text-text-secondary">
            <Avatar size="sm" className="h-[18px] w-[18px]">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span>{requestedBy}</span>
            <span className="text-text-muted">·</span>
            <span className="text-text-muted">{requestedDate}</span>
          </div>
        </div>
        <Badge variant={s.variant}>{s.label}</Badge>
      </header>
      {details ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 rounded-md border border-border bg-bg-elevated px-4 py-3 text-caption">
          {Object.entries(details).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-text-muted">{key}</dt>
              <dd className="text-text-primary">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {status === 'pending' ? (
        <footer className="flex items-center justify-end gap-2">
          {onReview ? (
            <Button type="button" variant="secondary" size="sm" onClick={onReview}>
              <Icon name="eye" size={12} />
              Review
            </Button>
          ) : null}
          {onReject ? (
            <Button type="button" variant="danger" size="sm" onClick={onReject}>
              <Icon name="x" size={12} />
              Reject
            </Button>
          ) : null}
          {onApprove ? (
            <Button type="button" variant="primary" size="sm" onClick={onApprove}>
              <Icon name="check" size={12} />
              Approve
            </Button>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}
